import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import axios from "axios";

const CART_KEY = "peptichill_cart_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

const emptyState = { items: [], updatedAt: 0 };

function loadFromStorage() {
  if (typeof window === "undefined") return emptyState;
  try {
    const raw = window.localStorage.getItem(CART_KEY);
    if (!raw) return emptyState;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return emptyState;
    return parsed;
  } catch {
    return emptyState;
  }
}

function persist(state) {
  try {
    window.localStorage.setItem(CART_KEY, JSON.stringify(state));
  } catch {
    /* no-op */
  }
}

export function CartProvider({ children }) {
  const [state, setState] = useState(loadFromStorage);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Persist on every state change.
  useEffect(() => {
    persist(state);
  }, [state]);

  // Fetch authoritative product info from the API (never trust client price).
  const fetchProduct = useCallback(async (slug) => {
    const res = await axios.get(`${API}/products/${slug}`);
    return res.data;
  }, []);

  const addItem = useCallback(async (slug, quantity = 1) => {
    const product = await fetchProduct(slug);
    setState((prev) => {
      const existing = prev.items.find((i) => i.slug === slug);
      const items = existing
        ? prev.items.map((i) =>
            i.slug === slug
              ? { ...i, quantity: Math.min(10, i.quantity + quantity) }
              : i,
          )
        : [
            ...prev.items,
            {
              slug: product.slug,
              name: product.name,
              price_pennies: product.price_pennies,
              image: product.images?.[0] || "",
              quantity: Math.min(10, quantity),
            },
          ];
      return { items, updatedAt: Date.now() };
    });
  }, [fetchProduct]);

  const updateQty = useCallback((slug, quantity) => {
    setState((prev) => {
      if (quantity <= 0) {
        return {
          items: prev.items.filter((i) => i.slug !== slug),
          updatedAt: Date.now(),
        };
      }
      return {
        items: prev.items.map((i) =>
          i.slug === slug ? { ...i, quantity: Math.min(10, quantity) } : i,
        ),
        updatedAt: Date.now(),
      };
    });
  }, []);

  const removeItem = useCallback((slug) => {
    setState((prev) => ({
      items: prev.items.filter((i) => i.slug !== slug),
      updatedAt: Date.now(),
    }));
  }, []);

  const clearCart = useCallback(() => {
    setState({ items: [], updatedAt: Date.now() });
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Light-touch server validation — refresh prices when the drawer or cart page opens.
  const validateWithServer = useCallback(async () => {
    if (state.items.length === 0) return null;
    try {
      const res = await axios.post(`${API}/cart/validate`, {
        items: state.items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
      });
      // Update local cart with any price changes from the server.
      const byslug = Object.fromEntries(
        (res.data.items || []).map((i) => [i.slug, i]),
      );
      setState((prev) => ({
        items: prev.items.map((i) =>
          byslug[i.slug]
            ? {
                ...i,
                name: byslug[i.slug].name,
                price_pennies: byslug[i.slug].price_pennies,
                image: byslug[i.slug].image,
              }
            : i,
        ),
        updatedAt: Date.now(),
      }));
      return res.data;
    } catch {
      return null;
    }
  }, [state.items]);

  const subtotalPennies = useMemo(
    () =>
      state.items.reduce(
        (sum, i) => sum + i.price_pennies * i.quantity,
        0,
      ),
    [state.items],
  );

  const itemCount = useMemo(
    () => state.items.reduce((sum, i) => sum + i.quantity, 0),
    [state.items],
  );

  const value = useMemo(
    () => ({
      items: state.items,
      subtotalPennies,
      itemCount,
      drawerOpen,
      openDrawer,
      closeDrawer,
      addItem,
      updateQty,
      removeItem,
      clearCart,
      validateWithServer,
    }),
    [
      state.items, subtotalPennies, itemCount, drawerOpen,
      openDrawer, closeDrawer, addItem, updateQty, removeItem, clearCart,
      validateWithServer,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
